'use client';

import Link from 'next/link';
import { getCdnUrl } from '@/utils/cdn';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const developments = [
  {
    id: 'park-alterosa',
    name: 'Park Alterosa',
    logo: getCdnUrl('/park-alterosa/logo-color.webp'),
    badge: 'Lançamento!',
    badgeColor: 'text-[#4ea075]',
    title: 'Conheça o Park Alterosa em São João del-Rei!',
    href: '/park-alterosa',
    buttonText: 'Conheça o Park Alterosa!',
    buttonBg: 'bg-[#17453a] hover:bg-[#205b4d]',
    iconBg: 'bg-[#ff7d2a]',
    images: [
      getCdnUrl('/park-alterosa/renders/externas/exterior-29.webp'),
      getCdnUrl('/park-alterosa/renders/internas/interior-7.webp'),
      getCdnUrl('/park-alterosa/renders/externas/exterior-4.webp'),
      getCdnUrl('/park-alterosa/renders/internas/interior-1.webp'),
      getCdnUrl('/park-alterosa/renders/externas/exterior-19.webp'),
      getCdnUrl('/park-alterosa/renders/internas/interior-6.webp'),
    ],
  },
  {
    id: 'park-europa',
    name: 'Park Europa',
    logo: getCdnUrl('/images/common/logo-park-europa-clean.webp'),
    badge: '',
    badgeColor: 'text-forbli-green',
    title: 'Conheça o Park Europa em Barbacena!',
    href: '/park-europa',
    buttonText: 'Conheça o Park Europa!',
    buttonBg: 'bg-forbli-green hover:bg-[#ccda43]',
    iconBg: 'bg-[#ccf03e]',
    images: [
      getCdnUrl('/images/header/foto1_header.webp'),
      getCdnUrl('/images/header/foto2_header.webp'),
      getCdnUrl('/images/header/foto3_header.webp'),
      getCdnUrl('/images/header/foto4_header.webp'),
    ],
  },
];

export default function HeroSection() {
  const [activeDevIndex, setActiveDevIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const activeDev = developments[activeDevIndex];
  const images = activeDev.images;

  // Reset image index when switching developments
  const handleSelectDev = (index: number) => {
    setActiveDevIndex(index);
    setCurrentImageIndex(0);
  };

  const goPrev = () => {
    setCurrentImageIndex((idx) => (idx === 0 ? images.length - 1 : idx - 1));
  };

  const goNext = () => {
    setCurrentImageIndex((idx) => (idx + 1) % images.length);
  };

  // Autoplay functionality for images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((idx) => (idx + 1) % images.length);
    }, 4500);

    return () => clearInterval(interval);
  }, [images.length, activeDevIndex]);

  return (
    <section className="relative bg-white sm:bg-gray-100 sm:pt-6 flex items-center justify-center pb-0 sm:pb-4 overflow-hidden lg:h-[90dvh]">
      <div className="mx-auto w-full sm:max-w-[85%] md:pt-28 rounded-none sm:rounded-t-3xl md:rounded-[40px] shadow-none sm:shadow-sm flex flex-col lg:flex-row m-0 bg-white lg:h-full">

        {/* Texto 35% */}
        <div className="w-full lg:w-[35%] md:px-16 px-6 py-8 md:py-0 relative z-10 rounded-none sm:rounded-t-3xl md:rounded-3xl bg-white flex flex-col justify-center">

          {/* Development Selector Tabs */}
          <div className="flex justify-center items-center gap-2 mb-4 bg-gray-100 p-1.5 rounded-full w-fit mx-auto">
            {developments.map((dev, idx) => (
              <button
                key={dev.id}
                onClick={() => handleSelectDev(idx)}
                className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 ${
                  idx === activeDevIndex
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {dev.name}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeDev.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center"
            >
              <div className="w-full max-w-xl flex justify-center h-20 items-center">
                <img
                  src={activeDev.logo}
                  alt={activeDev.name}
                  width={280}
                  height={80}
                  className="max-h-full w-auto object-contain"
                />
              </div>

              {activeDev.badge && (
                <p className={`md:text-[40px] text-[30px] text-center font-extrabold mt-2 ${activeDev.badgeColor}`}>
                  {activeDev.badge}
                </p>
              )}

              <p className="md:text-xl text-base md:text-left text-center font-light mt-2 text-gray-800">
                {activeDev.title}
              </p>

              <div className="mt-5 relative z-50">
                <Link
                  href={activeDev.href}
                  className={`${activeDev.buttonBg} text-white px-6 py-2.5 w-fit mx-auto rounded-full font-semibold transition-colors flex items-center gap-3 group text-sm shadow-md`}
                >
                  <span>{activeDev.buttonText}</span>
                  <div className={`w-9 h-9 ${activeDev.iconBg} rounded-full flex items-center justify-center transition-colors`}>
                    <svg
                      data-bbox="20 20 160 160"
                      viewBox="0 0 200 200"
                      width="48"
                      height="48"
                      xmlns="http://www.w3.org/2000/svg"
                      data-type="color"
                      className="w-10 h-10 sm:w-12 sm:h-12 group-hover:rotate-[300deg] transition-transform duration-300"
                    >
                      <g>
                        <path fill="#CCF03E" d="M180 100c0 44.183-35.817 80-80 80s-80-35.817-80-80 35.817-80 80-80 80 35.817 80 80z" data-color="1"></path>
                        <path d="M131.151 102.192c.008-.018.01-.037.017-.055.096-.256.155-.53.155-.82a2.34 2.34 0 0 0-.154-.818c-.007-.02-.011-.04-.019-.06a2.337 2.337 0 0 0-.452-.708c-.011-.012-.014-.027-.025-.039l-25.349-26.59a2.354 2.354 0 0 0-3.407 3.247l21.559 22.614H71.029a2.353 2.353 0 0 0 0 4.706h52.446l-21.558 22.612a2.354 2.354 0 0 0 3.407 3.248l25.349-26.59c.008-.009.011-.021.02-.031.193-.208.35-.448.458-.716z" fill="#020202" data-color="2"></path>
                      </g>
                    </svg>
                  </div>
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>

        </div>

        {/* Imagem 65% com selo do cadastro + setas do carrossel */}
        <div className="w-full lg:w-[65%] relative rounded-none sm:rounded-2xl">
          <div className="relative w-full h-[45dvh] lg:h-full rounded-none sm:rounded-3xl overflow-hidden bg-gray-100">
            <AnimatePresence mode="wait">
              <motion.img
                key={`${activeDev.id}-${currentImageIndex}`}
                src={images[currentImageIndex]}
                alt={`${activeDev.name} - imagem ${currentImageIndex + 1}`}
                className="absolute inset-0 w-full h-full object-cover rounded-none sm:rounded-3xl"
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent rounded-none sm:rounded-3xl pointer-events-none" />

            {/* Indicadores do carrossel */}
            <div className="absolute top-4 right-4 z-20 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  aria-label={`Ir para foto ${index + 1}`}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    index === currentImageIndex ? 'w-7 bg-white' : 'w-3 bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>

            {/* Controles do carrossel */}
            <div className="absolute md:bottom-4 bottom-32 left-1/2 -translate-x-1/2 z-30 flex items-center gap-56 sm:gap-64">
              <button
                onClick={goPrev}
                aria-label="Anterior"
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/90 text-gray-900 flex items-center justify-center shadow-lg transition-all hover:bg-white hover:scale-110 active:scale-95"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 sm:w-6 sm:h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
              </button>
              <button
                onClick={goNext}
                aria-label="Próximo"
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/90 text-gray-900 flex items-center justify-center shadow-lg transition-all hover:bg-white hover:scale-110 active:scale-95"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 sm:w-6 sm:h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </button>
            </div>
          </div>

          {/* Selo Minha Casa Minha Vida */}
          <div className="absolute -top-6 sm:top-20 right-0 w-40 sm:left-4 lg:top-[90%] lg:left-0 lg:-translate-x-1/2 lg:-translate-y-full z-20 pointer-events-none">
            <div className="grid place-items-center sm:hidden">
              <img src={getCdnUrl("/images/common/logo-mcmv-roda.webp")} alt="Minha Casa Minha Vida" width={120} height={120} className="animate-spin-slow [grid-area:1/1]" />
              <img src={getCdnUrl("/images/common/logo-mcmv.webp")} alt="Minha Casa Minha Vida" width={40} height={40} className="[grid-area:1/1]" />
            </div>
            <div className="hidden sm:grid place-items-center">
              <img src={getCdnUrl("/images/common/logo-mcmv-roda.webp")} alt="Minha Casa Minha Vida" width={140} height={140} className="animate-spin-slow [grid-area:1/1]" />
              <img src={getCdnUrl("/images/common/logo-mcmv.webp")} alt="Minha Casa Minha Vida" width={50} height={50} className="[grid-area:1/1]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
